import { useQuery } from "@tanstack/react-query";
import { privateAxios } from "../axiosInstances";
import ordersQueryKeys from "../query-keys/orders.query-keys";
import { EventTicketSale, PaginatedResponse, TicketOrderFilters, TicketOrderRow } from "@ac-expo/api-types";
import { isAxiosError } from "axios";

const getOrderInvoiceLink = async (orderId?: string) => {
  const { data } = await privateAxios.get<string>(
    `/orders/${orderId}/invoice-link`
  );
  return data;
};

export const useGetOrderInvoiceLinkQuery = (orderId?: string) => {
  return useQuery({
    queryKey: ordersQueryKeys.invoice(orderId),
    queryFn: () => getOrderInvoiceLink(orderId),
    enabled: orderId !== undefined,
  });
};

const getOrderProformaInvoiceLink = async (orderId?: string) => {
  const { data } = await privateAxios.get<string | null>(
    `/orders/${orderId}/proforma-invoice-link`
  );
  return data;
};

export const useGetOrderProformaInvoiceLinkQuery = (orderId?: string) => {
  return useQuery({
    queryKey: ordersQueryKeys.proforma(orderId),
    queryFn: () => getOrderProformaInvoiceLink(orderId),
    enabled: orderId !== undefined,
  });
};

const getExhibitorEventSales = async (eventId: string) => {
  try {
    const { data } = await privateAxios.get<EventTicketSale[]>(
      `/orders/events/${eventId}/sales/exhibitor`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useGetExhibitorEventSalesQuery = (eventId: string) => {
  return useQuery({
    queryKey: ordersQueryKeys.exhibitorEventSales(eventId),
    queryFn: () => getExhibitorEventSales(eventId),
  });
};

const getEventTicketOrders = async (eventId: string, filters: TicketOrderFilters) => {
  const { data } = await privateAxios.get<PaginatedResponse<TicketOrderRow>>(
    `/orders/events/${eventId}/ticket-orders`,
    { params: filters }
  );
  return data;
};

export const useGetEventTicketOrdersQuery = (eventId: string, filters: TicketOrderFilters) => {
  return useQuery({
    queryKey: ordersQueryKeys.ticketOrders(eventId, filters.search, filters.pageIndex, filters.pageSize),
    queryFn: () => getEventTicketOrders(eventId, filters),
  });
};
